import React, { useState, useEffect, useCallback } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  Plus, Trash2, X, Edit2, Truck, Wrench, BarChart3, Loader2, Eye, Fuel
} from 'lucide-react';

const fmt = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const emptyEq = () => ({
  equipment_code: '', name: '', name_arabic: '', category: '',
  plate_number: '', serial_number: '', ownership: 'owned',
  supplier_id: '', daily_rate: 0, hourly_rate: 0,
  purchase_date: '', purchase_cost: 0, status: 'active', notes: '',
});
const emptyUsage = () => ({
  equipment_id: '', project_id: '',
  usage_date: new Date().toISOString().slice(0, 10),
  hours: 0, fuel_liters: 0, fuel_cost: 0,
  operator_employee_id: '', notes: '', cost_center_id: '',
});

const STATUS_COLORS = {
  active: 'bg-emerald-100 text-emerald-700',
  idle: 'bg-gray-100 text-gray-600',
  under_maintenance: 'bg-amber-100 text-amber-700',
  disposed: 'bg-red-100 text-red-700',
};

const Equipment = ({ user }) => {
  const [tab, setTab] = useState('equipment');
  const [equipments, setEquipments] = useState([]);
  const [usage, setUsage] = useState([]);
  const [projects, setProjects] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [summary, setSummary] = useState(null);

  const [loading, setLoading] = useState(true);
  const [showEqModal, setShowEqModal] = useState(false);
  const [showUsageModal, setShowUsageModal] = useState(false);
  const [eqForm, setEqForm] = useState(emptyEq());
  const [usageForm, setUsageForm] = useState(emptyUsage());
  const [editingEqId, setEditingEqId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const isAdmin = (user?.role || '').toLowerCase() === 'admin';

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [eqRes, uRes, prRes, sRes, eRes] = await Promise.all([
        axiosInstance.get('/equipment'),
        axiosInstance.get('/equipment-usage'),
        axiosInstance.get('/projects'),
        axiosInstance.get('/suppliers'),
        axiosInstance.get('/employees').catch(() => ({ data: [] })),
      ]);
      setEquipments(eqRes.data);
      setUsage(uRes.data);
      setProjects(prRes.data);
      setSuppliers(sRes.data);
      setEmployees(eRes.data);
    } catch (e) {
      toast.error(`فشل التحميل: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Equipment CRUD ────────────────────────────────────────────────────
  const openCreateEq = () => { setEqForm(emptyEq()); setEditingEqId(null); setShowEqModal(true); };
  const openEditEq = (row) => {
    setEqForm({
      equipment_code: row.equipment_code || '', name: row.name || '', name_arabic: row.name_arabic || '',
      category: row.category || '', plate_number: row.plate_number || '', serial_number: row.serial_number || '',
      ownership: row.ownership || 'owned', supplier_id: row.supplier_id || '',
      daily_rate: row.daily_rate || 0, hourly_rate: row.hourly_rate || 0,
      purchase_date: row.purchase_date || '', purchase_cost: row.purchase_cost || 0,
      status: row.status || 'active', notes: row.notes || '',
    });
    setEditingEqId(row.id);
    setShowEqModal(true);
  };

  const submitEq = async (e) => {
    e.preventDefault();
    if (!eqForm.equipment_code || !eqForm.name) return toast.error('الكود والاسم مطلوبان');
    setSubmitting(true);
    try {
      const payload = {
        ...eqForm,
        supplier_id: eqForm.supplier_id || null,
        purchase_date: eqForm.purchase_date || null,
        daily_rate: Number(eqForm.daily_rate || 0),
        hourly_rate: Number(eqForm.hourly_rate || 0),
        purchase_cost: Number(eqForm.purchase_cost || 0),
      };
      if (editingEqId) {
        await axiosInstance.put(`/equipment/${editingEqId}`, payload);
        toast.success('تم تحديث المعدة');
      } else {
        await axiosInstance.post('/equipment', payload);
        toast.success('تم إنشاء المعدة');
      }
      setShowEqModal(false);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فشل الحفظ');
    } finally { setSubmitting(false); }
  };

  const deleteEq = async (id) => {
    if (!window.confirm('حذف المعدة؟')) return;
    try { await axiosInstance.delete(`/equipment/${id}`); toast.success('تم الحذف'); fetchAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'فشل'); }
  };

  // ── Usage CRUD ────────────────────────────────────────────────────────
  const submitUsage = async (e) => {
    e.preventDefault();
    if (!usageForm.equipment_id) return toast.error('المعدة مطلوبة');
    setSubmitting(true);
    try {
      await axiosInstance.post('/equipment-usage', {
        ...usageForm,
        project_id: usageForm.project_id || null,  // null = auto-allocate via rule engine
        hours: Number(usageForm.hours || 0),
        fuel_liters: Number(usageForm.fuel_liters || 0),
        fuel_cost: Number(usageForm.fuel_cost || 0),
        operator_employee_id: usageForm.operator_employee_id || null,
        cost_center_id: usageForm.cost_center_id || null,
      });
      toast.success('تم تسجيل التشغيل');
      setShowUsageModal(false);
      setUsageForm(emptyUsage());
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فشل');
    } finally { setSubmitting(false); }
  };

  const deleteUsage = async (id) => {
    if (!window.confirm('حذف سجل التشغيل؟ سيُعكس القيد المحاسبي.')) return;
    try { await axiosInstance.delete(`/equipment-usage/${id}`); toast.success('تم الحذف'); fetchAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'فشل'); }
  };

  const viewSummary = async (id) => {
    try {
      const res = await axiosInstance.get(`/equipment/${id}/summary`);
      setSummary(res.data);
    } catch (e) { toast.error('فشل'); }
  };

  return (
    <div className="space-y-6" data-testid="equipment-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Truck className="w-7 h-7 text-slate-700" /> Equipment & Machinery
          </h1>
          <p className="text-sm text-gray-500 mt-1">المعدات والآليات — تتبع التكلفة لكل مشروع</p>
        </div>
        <div className="flex gap-2">
          {tab === 'equipment' && isAdmin && (
            <button data-testid="new-eq-btn" onClick={openCreateEq}
              className="px-4 py-2 rounded-lg bg-slate-700 text-white text-sm hover:bg-slate-800 flex items-center gap-2">
              <Plus className="w-4 h-4" /> New Equipment
            </button>
          )}
          {tab === 'usage' && (
            <button data-testid="new-usage-btn" onClick={() => { setUsageForm(emptyUsage()); setShowUsageModal(true); }}
              className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm hover:bg-emerald-700 flex items-center gap-2">
              <Plus className="w-4 h-4" /> Log Usage
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200">
        {[
          { k: 'equipment', label: 'Equipment Master', icon: Truck },
          { k: 'usage', label: 'Usage Log', icon: Wrench },
        ].map(({ k, label, icon: Icon }) => (
          <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)}
            className={`px-4 py-2 text-sm font-medium flex items-center gap-2 border-b-2 -mb-px ${
              tab === k ? 'border-slate-700 text-slate-800' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}>
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-gray-500 flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading...</div>
        ) : tab === 'equipment' ? (
          equipments.length === 0 ? (
            <div className="p-10 text-center text-gray-400">لا توجد معدات بعد. أنشئ أول معدة لتبدأ.</div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-[11px] uppercase text-gray-500 font-semibold">Code</th>
                  <th className="px-3 py-2 text-left text-[11px] uppercase text-gray-500 font-semibold">Name</th>
                  <th className="px-3 py-2 text-left text-[11px] uppercase text-gray-500 font-semibold">Category</th>
                  <th className="px-3 py-2 text-left text-[11px] uppercase text-gray-500 font-semibold">Plate</th>
                  <th className="px-3 py-2 text-left text-[11px] uppercase text-gray-500 font-semibold">Ownership</th>
                  <th className="px-3 py-2 text-right text-[11px] uppercase text-gray-500 font-semibold">Hourly</th>
                  <th className="px-3 py-2 text-center text-[11px] uppercase text-gray-500 font-semibold">Status</th>
                  <th className="px-3 py-2 text-right text-[11px] uppercase text-gray-500 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {equipments.map((eq) => (
                  <tr key={eq.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-3 py-2 font-mono text-sm">{eq.equipment_code}</td>
                    <td className="px-3 py-2 text-sm">{eq.name} {eq.name_arabic && <span className="text-gray-500 text-xs">/ {eq.name_arabic}</span>}</td>
                    <td className="px-3 py-2 text-sm text-gray-600">{eq.category || '—'}</td>
                    <td className="px-3 py-2 text-sm font-mono text-gray-600">{eq.plate_number || '—'}</td>
                    <td className="px-3 py-2 text-sm capitalize">{eq.ownership}</td>
                    <td className="px-3 py-2 text-sm text-right font-mono">{fmt(eq.hourly_rate)}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`text-xs px-2 py-0.5 rounded ${STATUS_COLORS[eq.status] || 'bg-gray-100'}`}>{eq.status}</span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex justify-end gap-1">
                        <button data-testid={`summary-eq-${eq.equipment_code}`} onClick={() => viewSummary(eq.id)}
                          className="p-1.5 hover:bg-gray-100 rounded text-blue-600" title="Summary"><BarChart3 className="w-4 h-4" /></button>
                        {isAdmin && (
                          <>
                            <button data-testid={`edit-eq-${eq.equipment_code}`} onClick={() => openEditEq(eq)}
                              className="p-1.5 hover:bg-gray-100 rounded text-emerald-600"><Edit2 className="w-4 h-4" /></button>
                            <button data-testid={`delete-eq-${eq.equipment_code}`} onClick={() => deleteEq(eq.id)}
                              className="p-1.5 hover:bg-gray-100 rounded text-red-600"><Trash2 className="w-4 h-4" /></button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        ) : (
          usage.length === 0 ? (
            <div className="p-10 text-center text-gray-400">لا توجد سجلات تشغيل بعد.</div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-[11px] uppercase text-gray-500 font-semibold">#</th>
                  <th className="px-3 py-2 text-left text-[11px] uppercase text-gray-500 font-semibold">Date</th>
                  <th className="px-3 py-2 text-left text-[11px] uppercase text-gray-500 font-semibold">Equipment</th>
                  <th className="px-3 py-2 text-left text-[11px] uppercase text-gray-500 font-semibold">Project</th>
                  <th className="px-3 py-2 text-right text-[11px] uppercase text-gray-500 font-semibold">Hours</th>
                  <th className="px-3 py-2 text-right text-[11px] uppercase text-gray-500 font-semibold">Hours Cost</th>
                  <th className="px-3 py-2 text-right text-[11px] uppercase text-gray-500 font-semibold">Fuel L</th>
                  <th className="px-3 py-2 text-right text-[11px] uppercase text-gray-500 font-semibold">Fuel Cost</th>
                  <th className="px-3 py-2 text-left text-[11px] uppercase text-gray-500 font-semibold">Operator</th>
                  <th className="px-3 py-2 text-left text-[11px] uppercase text-gray-500 font-semibold">Journal</th>
                  <th className="px-3 py-2 text-right text-[11px] uppercase text-gray-500 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {usage.map((u) => (
                  <tr key={u.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-3 py-2 font-mono text-xs">{u.usage_number}</td>
                    <td className="px-3 py-2 text-sm">{u.usage_date}</td>
                    <td className="px-3 py-2 text-sm">{u.equipment_code} — {u.equipment_name}</td>
                    <td className="px-3 py-2 text-sm">{u.project_name}</td>
                    <td className="px-3 py-2 text-sm text-right font-mono">{fmt(u.hours)}</td>
                    <td className="px-3 py-2 text-sm text-right font-mono text-blue-700">{fmt(u.hours_cost)}</td>
                    <td className="px-3 py-2 text-sm text-right font-mono">{fmt(u.fuel_liters)}</td>
                    <td className="px-3 py-2 text-sm text-right font-mono text-orange-600">{fmt(u.fuel_cost)}</td>
                    <td className="px-3 py-2 text-sm">{u.operator_name || '—'}</td>
                    <td className="px-3 py-2 text-xs font-mono text-gray-500">{u.journal_no || '—'}</td>
                    <td className="px-3 py-2 text-right">
                      {isAdmin && (
                        <button data-testid={`delete-usage-${u.usage_number}`} onClick={() => deleteUsage(u.id)}
                          className="p-1.5 hover:bg-gray-100 rounded text-red-600"><Trash2 className="w-4 h-4" /></button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        )}
      </div>

      {/* Equipment Modal */}
      {showEqModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowEqModal(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[92vh] overflow-y-auto" data-testid="eq-modal">
            <div className="px-5 py-3 border-b flex items-center justify-between sticky top-0 bg-white">
              <h3 className="font-semibold">{editingEqId ? 'Edit' : 'New'} Equipment</h3>
              <button onClick={() => setShowEqModal(false)} className="p-1 hover:bg-gray-100 rounded"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submitEq} className="px-5 py-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">Code *</label>
                  <input data-testid="eq-code" required value={eqForm.equipment_code} onChange={(e) => setEqForm({ ...eqForm, equipment_code: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm font-mono" />
                </div>
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">Name *</label>
                  <input data-testid="eq-name" required value={eqForm.name} onChange={(e) => setEqForm({ ...eqForm, name: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm" />
                </div>
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">Arabic Name</label>
                  <input value={eqForm.name_arabic} onChange={(e) => setEqForm({ ...eqForm, name_arabic: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm" />
                </div>
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">Category</label>
                  <input list="cat-list" value={eqForm.category} onChange={(e) => setEqForm({ ...eqForm, category: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm" />
                  <datalist id="cat-list">
                    <option value="Excavator" /><option value="Bulldozer" /><option value="Loader" />
                    <option value="Crane" /><option value="Truck" /><option value="Concrete Mixer" />
                    <option value="Forklift" /><option value="Compactor" /><option value="Generator" />
                  </datalist>
                </div>
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">Plate #</label>
                  <input value={eqForm.plate_number} onChange={(e) => setEqForm({ ...eqForm, plate_number: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm font-mono" />
                </div>
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">Serial #</label>
                  <input value={eqForm.serial_number} onChange={(e) => setEqForm({ ...eqForm, serial_number: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm font-mono" />
                </div>
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">Ownership</label>
                  <select value={eqForm.ownership} onChange={(e) => setEqForm({ ...eqForm, ownership: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm">
                    <option value="owned">Owned</option>
                    <option value="rented">Rented</option>
                    <option value="leased">Leased</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">Supplier / Renter</label>
                  <select value={eqForm.supplier_id} onChange={(e) => setEqForm({ ...eqForm, supplier_id: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm">
                    <option value="">— None —</option>
                    {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name_english || s.name_arabic}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">Hourly Rate (SAR)</label>
                  <input type="number" step="0.01" value={eqForm.hourly_rate} onChange={(e) => setEqForm({ ...eqForm, hourly_rate: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm font-mono text-right" />
                </div>
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">Daily Rate (SAR)</label>
                  <input type="number" step="0.01" value={eqForm.daily_rate} onChange={(e) => setEqForm({ ...eqForm, daily_rate: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm font-mono text-right" />
                </div>
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">Purchase Date</label>
                  <input type="date" value={eqForm.purchase_date} onChange={(e) => setEqForm({ ...eqForm, purchase_date: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm" />
                </div>
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">Purchase Cost (SAR)</label>
                  <input type="number" step="0.01" value={eqForm.purchase_cost} onChange={(e) => setEqForm({ ...eqForm, purchase_cost: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm font-mono text-right" />
                </div>
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">Status</label>
                  <select value={eqForm.status} onChange={(e) => setEqForm({ ...eqForm, status: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm">
                    <option value="active">Active</option>
                    <option value="idle">Idle</option>
                    <option value="under_maintenance">Under Maintenance</option>
                    <option value="disposed">Disposed</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-600 mb-1 block">Notes</label>
                <textarea rows={2} value={eqForm.notes} onChange={(e) => setEqForm({ ...eqForm, notes: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowEqModal(false)} className="px-3 py-1.5 text-sm border rounded hover:bg-gray-50">Cancel</button>
                <button type="submit" data-testid="eq-submit" disabled={submitting}
                  className="px-4 py-1.5 text-sm bg-slate-700 text-white rounded hover:bg-slate-800 disabled:opacity-50">
                  {submitting ? 'Saving…' : (editingEqId ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Usage Modal */}
      {showUsageModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowUsageModal(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg shadow-xl max-w-lg w-full" data-testid="usage-modal">
            <div className="px-5 py-3 border-b flex items-center justify-between">
              <h3 className="font-semibold flex items-center gap-2"><Wrench className="w-4 h-4" /> Log Equipment Usage</h3>
              <button onClick={() => setShowUsageModal(false)} className="p-1 hover:bg-gray-100 rounded"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submitUsage} className="px-5 py-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">Equipment *</label>
                  <select data-testid="usage-equipment" required value={usageForm.equipment_id} onChange={(e) => setUsageForm({ ...usageForm, equipment_id: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm">
                    <option value="">— Select —</option>
                    {equipments.filter((e) => e.status === 'active').map((e) => (
                      <option key={e.id} value={e.id}>{e.equipment_code} — {e.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">Project (optional — auto-allocated if empty)</label>
                  <select data-testid="usage-project" value={usageForm.project_id} onChange={(e) => setUsageForm({ ...usageForm, project_id: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm">
                    <option value="">— Auto (rule engine) —</option>
                    {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">Date *</label>
                  <input type="date" required value={usageForm.usage_date} onChange={(e) => setUsageForm({ ...usageForm, usage_date: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm" />
                </div>
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">Operator</label>
                  <select value={usageForm.operator_employee_id} onChange={(e) => setUsageForm({ ...usageForm, operator_employee_id: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm">
                    <option value="">— None —</option>
                    {employees.map((e) => <option key={e.id} value={e.id}>{e.name || e.name_english || e.name_arabic}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">Hours</label>
                  <input data-testid="usage-hours" type="number" step="0.01" value={usageForm.hours} onChange={(e) => setUsageForm({ ...usageForm, hours: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm font-mono text-right" />
                </div>
                <div>
                  <label className="text-xs text-gray-600 mb-1 block flex items-center gap-1"><Fuel className="w-3 h-3" /> Fuel Liters</label>
                  <input type="number" step="0.01" value={usageForm.fuel_liters} onChange={(e) => setUsageForm({ ...usageForm, fuel_liters: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm font-mono text-right" />
                </div>
                <div className="col-span-2">
                  <label className="text-xs text-gray-600 mb-1 block">Fuel Cost (SAR) <span className="text-gray-400">— auto-posts to Cost of Equipment / Petty Cash</span></label>
                  <input data-testid="usage-fuel-cost" type="number" step="0.01" value={usageForm.fuel_cost} onChange={(e) => setUsageForm({ ...usageForm, fuel_cost: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm font-mono text-right" />
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-600 mb-1 block">Notes</label>
                <textarea rows={2} value={usageForm.notes} onChange={(e) => setUsageForm({ ...usageForm, notes: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowUsageModal(false)} className="px-3 py-1.5 text-sm border rounded hover:bg-gray-50">Cancel</button>
                <button type="submit" data-testid="usage-submit" disabled={submitting}
                  className="px-4 py-1.5 text-sm bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50">
                  {submitting ? 'Saving…' : 'Log Usage'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Summary Modal */}
      {summary && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSummary(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[92vh] overflow-y-auto">
            <div className="px-5 py-3 border-b flex items-center justify-between">
              <h3 className="font-semibold flex items-center gap-2">
                <BarChart3 className="w-5 h-5" /> {summary.equipment_code} — {summary.equipment_name}
              </h3>
              <button onClick={() => setSummary(null)} className="p-1 hover:bg-gray-100 rounded"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr><th className="px-3 py-2 text-left">Project</th>
                    <th className="px-3 py-2 text-right">Hours</th>
                    <th className="px-3 py-2 text-right">Hours Cost</th>
                    <th className="px-3 py-2 text-right">Fuel L</th>
                    <th className="px-3 py-2 text-right">Fuel Cost</th>
                    <th className="px-3 py-2 text-right">Total</th></tr>
                </thead>
                <tbody>
                  {summary.by_project.length === 0 && <tr><td colSpan={6} className="px-3 py-4 text-center text-gray-400">لا توجد سجلات بعد</td></tr>}
                  {summary.by_project.map((r) => (
                    <tr key={r.project_id} className="border-t border-gray-100">
                      <td className="px-3 py-2">{r.project_name}</td>
                      <td className="px-3 py-2 text-right font-mono">{fmt(r.hours)}</td>
                      <td className="px-3 py-2 text-right font-mono text-blue-700">{fmt(r.hours_cost)}</td>
                      <td className="px-3 py-2 text-right font-mono">{fmt(r.fuel_liters)}</td>
                      <td className="px-3 py-2 text-right font-mono text-orange-600">{fmt(r.fuel_cost)}</td>
                      <td className="px-3 py-2 text-right font-mono font-semibold">{fmt(r.total_cost)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50 font-semibold">
                  <tr>
                    <td className="px-3 py-2">Total</td>
                    <td className="px-3 py-2 text-right font-mono">{fmt(summary.totals.hours)}</td>
                    <td className="px-3 py-2 text-right font-mono text-blue-700">{fmt(summary.totals.hours_cost)}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmt(summary.totals.fuel_liters)}</td>
                    <td className="px-3 py-2 text-right font-mono text-orange-600">{fmt(summary.totals.fuel_cost)}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmt(summary.totals.total_cost)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Equipment;
